import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { auth, supabase } from '@/api';
import { sameObject } from '@/lib/sameData';

const AuthContext = createContext(null);

// Clés localStorage à purger au logout / SIGNED_OUT pour empêcher qu'un
// utilisateur suivant sur le même iPad accède aux PII (tel, email, BCE) du
// précédent. Ne purge pas les préférences UI non sensibles (filtres).
const SENSITIVE_LOCAL_KEYS = [
  'renouv-last-fiche-v1',
];
// Préfixes de clés à purger en bloc (brouillons de notes par fiche).
const SENSITIVE_LOCAL_PREFIXES = [
  'renouv-note-draft-',
];
function purgeRenouLocalCache() {
  try {
    for (const k of SENSITIVE_LOCAL_KEYS) localStorage.removeItem(k);
    // Itère sur l'index pour trouver les clés préfixées (brouillons par fiche)
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && SENSITIVE_LOCAL_PREFIXES.some(p => k.startsWith(p))) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
  } catch { /* private mode / quota */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);

  // `isLoadingAuth` fait afficher l'écran « Initialisation… » plein écran, qui
  // DÉMONTE tout l'arbre (DataProvider compris). Seule la toute première
  // résolution a le droit de le déclencher : Supabase rejoue TOKEN_REFRESHED à
  // chaque renouvellement de jeton et SIGNED_IN au retour sur l'onglet — ces
  // événements repassaient par ici, l'écran de chargement revenait, toutes les
  // données étaient rechargées, et l'interface clignotait en boucle.
  const authResolueRef = useRef(false);

  const refreshUser = useCallback(async () => {
    try {
      setAuthError(null);
      if (!authResolueRef.current) setIsLoadingAuth(true);
      const me = await auth.me();
      // Identité conservée quand le profil n'a pas bougé : sans ça, chaque
      // renouvellement de jeton invalidait tous les calculs dérivés des leads.
      setUser((prev) => (sameObject(prev, me) ? prev : me));
      setIsAuthenticated(true);
    } catch (err) {
      setUser(null);
      setIsAuthenticated(false);
      if (err.status === 401) {
        setAuthError({ type: 'auth_required', message: 'Authentication required' });
      } else {
        setAuthError({ type: 'unknown', message: err.message });
      }
    } finally {
      authResolueRef.current = true;
      setIsLoadingAuth(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoadingAuth(false);
        // Prochaine connexion = nouveau premier chargement, l'écran
        // « Initialisation… » a de nouveau du sens.
        authResolueRef.current = false;
        purgeRenouLocalCache();
      } else if (
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'USER_UPDATED'
      ) {
        refreshUser();
      }
    });

    return () => data?.subscription?.unsubscribe();
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch (err) {
      // Même si signOut échoue (réseau, session expirée), on purge l'état
      // local pour ne pas laisser l'UI dans un état connecté incohérent.
      console.error('logout: signOut a échoué, purge locale quand même', err);
    } finally {
      purgeRenouLocalCache();
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const navigateToLogin = useCallback(async () => {
    await auth.redirectToLogin(window.location.origin);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        isLoadingPublicSettings: false,
        authError,
        logout,
        navigateToLogin,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
